import { Inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  filter,
  map,
  of,
  take,
  tap,
} from 'rxjs';
import {
  WeekDay,
  WeeklyMenu,
  WeeklyMenuReqParams,
} from '../api/contracts/menu';
import { HttpMethods } from '../api/api.enums';
import { environment } from 'src/environments/environment';
import { InStoreProduct, Meal, Product, ProductPrice } from '../api/contracts/product';
import { WeekIndex } from '../types/menu';
import {
  getDayOfTheWeek as getTodaysDayNumberOfTheWeek,
  getWeekFirstDay,
} from '../utils/date.utils';
import { APP_CONFIG, AppConfig } from '../config/app-config';
import { WEEK_DAYS } from 'src/constants/date.constants';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class MealMenuService {
  private readonly _weeklyMenus$ = new BehaviorSubject<WeeklyMenu[] | null>(
    null
  );
  public readonly weeklyMenus$: Observable<WeeklyMenu[] | null>;

  public readonly selectedWeeklyMenu$ = new BehaviorSubject<WeeklyMenu | null>(
    null
  );

  constructor(
    private readonly apiService: ApiService,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig
  ) {
    this.weeklyMenus$ = this._weeklyMenus$.asObservable();
  }

  retrieveWeeklyMenus(params?: WeeklyMenuReqParams): Observable<WeeklyMenu[]> {
    return this.apiService
      .request<WeeklyMenu[], WeeklyMenuReqParams>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/weekly_menus`,
        params,
      })
      .pipe(
        map((res) => res.body as WeeklyMenu[]),
        tap((menu) => this._weeklyMenus$.next(menu))
      );
  }

  retrieveMeal(mealId: string): Observable<Meal> {
    return this.apiService
      .request<Meal>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/meals/${mealId}`,
      })
      .pipe(map((res) => res.body as Meal));
  }

  retrieveInStoreProducts(locationId: string): Observable<InStoreProduct[]> {
    return this.apiService
      .request<InStoreProduct[]>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/store_products/${locationId}`,
      })
      .pipe(map((res) => res.body as InStoreProduct[]));
  }

  retrieveInStoreProduct(
    productId: number,
    locationId: string
  ): Observable<InStoreProduct> {
    return this.apiService
      .request<InStoreProduct>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/store_products/${locationId}/${productId}`,
      })
      .pipe(map((res) => res.body as InStoreProduct));
  }

  findMenuForWeek(menus: WeeklyMenu[], weekNumber: number): WeeklyMenu | null {
    const menu = menus?.find((menu) => menu.weekNumber === weekNumber);
    if (!menu) {
      return null;
    }
    return menu;
  }

  findFirstAvailableDayToOrder(weekIndex: WeekIndex): WeekDay {
    if (weekIndex === WeekIndex.THIS_WEEK) {
      let todaysDayIndex = getTodaysDayNumberOfTheWeek();
      const now = new Date();
      if (now.getHours() >= this.appConfig.stopReceivingOrdersAt) {
        todaysDayIndex++;
      }
      return WEEK_DAYS[todaysDayIndex + 1];
    }

    if (weekIndex === WeekIndex.NEXT_WEEK) {
      let nextWeekStartsFrom = WEEK_DAYS.indexOf(getWeekFirstDay());
      const lastDayAvailableToOrder = WEEK_DAYS.indexOf('sun');
      const now = moment();
      if (
        now.weekday() >= lastDayAvailableToOrder &&
        now.hours() >= this.appConfig.stopReceivingOrdersAt
      ) {
        nextWeekStartsFrom++;
      }
      return WEEK_DAYS[nextWeekStartsFrom];
    }
    return WEEK_DAYS[0];
  }

  isAnyDayAvailableToOrderThisWeek(menu: WeeklyMenu): boolean {
    let todaysDayIndex = getTodaysDayNumberOfTheWeek();
    const now = new Date();
    if (now.getHours() >= this.appConfig.stopReceivingOrdersAt) {
      todaysDayIndex++;
    }
    const daysRemaning = WEEK_DAYS.slice(todaysDayIndex + 1);
    return (
      daysRemaning.reduce((acc, day) => {
        acc += menu!.days[day]?.meals.length || 0;
        return acc;
      }, 0) > 0
    );
  }

  getRelevantProductPrice(product: Product): ProductPrice {
    const mainCurrency = this.appConfig.mainCurrency;
    if (product?.discountedPrices && product.discountedPrices[mainCurrency] > 0) {
      return product.discountedPrices;
    }
    return product.prices;
  }
}
